import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@vp/swan';
import { useChatActions, useChatInput, useChatMessages } from '../contexts/ChatContext';
import { getSelector } from '../selectors/registry';
import { BriefReadyCard } from './BriefReadyCard';
import { ChatInputField } from './ChatInputField';
import { MaxMessagesReachedCard } from './MaxMessagesReachedCard';
import type { SelectorType } from '../selectors/registry';
import type { ParsedComponent } from '../types/chat';
import type { ResizableTextAreaRef } from './ResizableTextArea';

interface ChatInputAreaProps {
    /** Called when the user clicks "Fill out a form instead". When undefined the prompt is hidden. */
    onSwitchToForm?: () => void;
    /** Called when the user clicks "Review your brief" on the completion card */
    onReview: () => void;
}

/**
 * Single dispatch point for all selector inputs. Adding a new selector type
 * requires only a new entry in SELECTOR_REGISTRY — no changes here.
 */
function SelectorRenderer({
    component,
    onComplete,
    designBrief,
}: {
    component: ParsedComponent;
    designBrief: ReturnType<typeof useChatActions>['designBrief'];
    onComplete: <T extends SelectorType>(
        type: T,
        result: ReturnType<typeof getSelector<T>>['completionFor'] extends (
            r: infer R,
            ...args: never[]
        ) => unknown
            ? R
            : never
    ) => void;
}) {
    const { printProductMpvId } = useChatActions();
    // The descriptor's render() is typed against its own variant of ParsedComponent,
    // so we narrow via the discriminant and let each branch pass the strongly typed
    // data through. The list is exhaustive by construction.
    switch (component.type) {
        case 'style-tiles':
            return getSelector('style-tiles').render(component.data, {
                onComplete: (r) => onComplete('style-tiles', r),
                designBrief,
            });
        case 'color-selector':
            return getSelector('color-selector').render(component.data, {
                onComplete: (r) => onComplete('color-selector', r),
                designBrief,
            });
        case 'icon-tiles':
            return getSelector('icon-tiles').render(component.data, {
                onComplete: (r) => onComplete('icon-tiles', r),
                designBrief,
            });
        case 'review-method':
            return getSelector('review-method').render(component.data, {
                onComplete: (r) => onComplete('review-method', r),
                designBrief,
            });
        case 'product-options':
            return getSelector('product-options').render(
                { ...component.data, mpvId: printProductMpvId, tenant: 'vistaprint' },
                { onComplete: (r) => onComplete('product-options', r), designBrief }
            );
        case 'reference-projects':
            return getSelector('reference-projects').render(component.data, {
                onComplete: (r) => onComplete('reference-projects', r),
                designBrief,
            });
    }
}

export function ChatInputArea({ onSwitchToForm, onReview }: ChatInputAreaProps) {
    const { messages, isLoading, maxMessages, retryCount } = useChatMessages();
    const isPersistentError = retryCount >= 2;
    const { inputText, onInputChange, attachments, onAttachmentsAdd, onAttachmentRemove, onSend } =
        useChatInput();
    const { briefComplete, onSelectorComplete, designBrief } = useChatActions();
    const lastMessage = messages[messages.length - 1];
    const componentRequest =
        lastMessage?.type === 'componentRequest' ? lastMessage.component : null;
    const lastHasComponent = !!componentRequest;
    const inputFieldRef = useRef<ResizableTextAreaRef>(null);
    const wasLoadingRef = useRef(false);
    const [continuedChat, setContinuedChat] = useState(false);

    const totalMessageCount = messages.filter((m) => m.type === 'text').length;
    const maxMessagesReached =
        maxMessages !== null && totalMessageCount >= maxMessages && !isLoading;

    // Refocus text input after AI reply completes (loading → not loading, no selector active)
    useEffect(() => {
        if (
            wasLoadingRef.current &&
            !isLoading &&
            !componentRequest &&
            (!briefComplete || continuedChat) &&
            !maxMessagesReached
        ) {
            inputFieldRef.current?.focus();
        }
        wasLoadingRef.current = isLoading;
    }, [isLoading, componentRequest, briefComplete, continuedChat, maxMessagesReached]);

    return (
        <Box
            style={{
                flexShrink: 0,
                borderTop: '1px solid var(--swan-sem-color-border-subtle)',
                backgroundColor: 'var(--swan-sem-color-bg-info)',
            }}
        >
            <Box
                style={{
                    maxWidth: '768px',
                    margin: '0 auto',
                    padding:
                        (briefComplete && !isLoading && !continuedChat) ||
                        maxMessagesReached ||
                        lastHasComponent
                            ? '0'
                            : 'var(--swan-sem-space-5) var(--swan-sem-space-5) var(--swan-sem-space-3)',
                }}
            >
                {componentRequest ? (
                    <Box>
                        <SelectorRenderer
                            component={componentRequest}
                            onComplete={onSelectorComplete}
                            designBrief={designBrief}
                        />
                    </Box>
                ) : briefComplete && !isLoading && !continuedChat ? (
                    <BriefReadyCard
                        onReview={onReview}
                        onContinue={!maxMessagesReached ? () => setContinuedChat(true) : undefined}
                    />
                ) : maxMessagesReached ? (
                    <MaxMessagesReachedCard onReview={onReview} />
                ) : (
                    <>
                        {briefComplete && (
                            <Box
                                style={{
                                    marginBottom: 'var(--swan-sem-space-5)',
                                    textAlign: 'center',
                                }}
                            >
                                <Typography fontSkin="body-standard">
                                    Your brief is ready!{' '}
                                    <button
                                        type="button"
                                        onClick={onReview}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            color: 'inherit',
                                            font: 'inherit',
                                        }}
                                    >
                                        Review now
                                    </button>
                                </Typography>
                            </Box>
                        )}
                        {onSwitchToForm && messages.length <= 1 && (
                            <Box
                                style={{
                                    marginBottom: 'var(--swan-sem-space-5)',
                                    textAlign: 'center',
                                }}
                            >
                                <Typography fontSkin="body-standard">
                                    Prefer not to chat?{' '}
                                    <button
                                        type="button"
                                        onClick={onSwitchToForm}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            color: 'inherit',
                                            font: 'inherit',
                                        }}
                                    >
                                        Fill out a form instead
                                    </button>
                                </Typography>
                            </Box>
                        )}
                        <ChatInputField
                            value={inputText}
                            onChange={onInputChange}
                            onSend={onSend}
                            attachments={attachments}
                            onAttachmentsAdd={onAttachmentsAdd}
                            onAttachmentRemove={onAttachmentRemove}
                            disabled={isLoading || isPersistentError}
                            inputRef={inputFieldRef}
                            placeholder={
                                messages.length > 1
                                    ? 'Type your answer here'
                                    : 'For example, I need a unique design to promote my dog grooming business'
                            }
                        />
                    </>
                )}
            </Box>
        </Box>
    );
}
